import { createClient } from '@supabase/supabase-js'
import type { Database } from './database.types'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

// Maximum number of retry attempts
const MAX_RETRIES = 3;
// Base delay in milliseconds before retrying (will be multiplied by attempt number)
const BASE_RETRY_DELAY = 1000;

// Enhanced error handling for Supabase connection
const options = {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
  },
  global: {
    fetch: async (url: RequestInfo | URL, options?: RequestInit) => {
      let retries = 0;
      
      while (retries < MAX_RETRIES) {
        try {
          const response = await fetch(url, options);
          
          // Check for rate limiting or server errors
          if (response.status === 429 || (response.status >= 500 && response.status < 600)) {
            const delay = BASE_RETRY_DELAY * (retries + 1);
            console.warn(`Supabase request failed with status ${response.status}. Retrying in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            retries++;
            continue;
          }
          
          return response;
        } catch (err) {
          if (retries < MAX_RETRIES - 1) {
            const delay = BASE_RETRY_DELAY * (retries + 1);
            console.error(`Supabase fetch error (attempt ${retries + 1}/${MAX_RETRIES}):`, err);
            console.warn(`Retrying in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            retries++;
          } else {
            console.error('Supabase fetch failed after multiple retries:', err);
            // Return a minimal response to prevent crashing
            return new Response(JSON.stringify({ 
              error: 'Network Error',
              message: 'Failed to connect to the database after multiple attempts. Please check your internet connection and try again.'
            }), {
              headers: { 'Content-Type': 'application/json' },
              status: 503
            });
          }
        }
      }
      
      // This should never be reached due to the return in the catch block,
      // but TypeScript needs it for type safety
      return new Response(JSON.stringify({ error: 'Unexpected error' }), {
        headers: { 'Content-Type': 'application/json' },
        status: 500
      });
    }
  }
}

export const supabase = createClient<Database>(
  supabaseUrl, 
  supabaseAnonKey,
  options
)

// Add connection health check function
export const checkSupabaseConnection = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.warn('Supabase connection issue during health check:', error);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Supabase connection health check failed:', error);
    return false;
  }
}

// Utility function to check if job_categories table exists
export const checkCategoriesTable = async () => {
  try {
    console.log('Checking job_categories table...');
    
    // Check if we can query the job_categories table
    const { data, error } = await supabase
      .from('job_categories')
      .select('id')
      .limit(1);
      
    if (error) {
      console.error('Error accessing job_categories table:', error);
      return {
        exists: false,
        error: error.message
      };
    }
    
    console.log('Successfully accessed job_categories table');
    
    // Introspect table structure
    const { data: tableInfo, error: introspectionError } = await supabase
      .rpc('get_table_structure', { table_name: 'job_categories' })
      .select();
      
    if (introspectionError) {
      console.error('Error getting table structure:', introspectionError);
      // We know the table exists because we could query it
      return {
        exists: true,
        structureKnown: false,
        error: introspectionError.message
      };
    }
    
    return {
      exists: true,
      structureKnown: true,
      structure: tableInfo
    };
  } catch (error) {
    console.error('Unexpected error checking categories table:', error);
    return {
      exists: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// Utility function to ensure the job_categories table exists
export const ensureCategoriesTable = async () => {
  try {
    // First check if the table exists
    const categoryCheck = await checkCategoriesTable();
    
    if (categoryCheck.exists) {
      console.log('Categories table already exists, no action needed');
      return true;
    }
    
    console.log('Creating job_categories table...');
    
    // Create the job_categories table
    const { error: createTableError } = await supabase.rpc('execute_sql', {
      sql: `
        -- Create the job_categories table if it doesn't exist
        CREATE TABLE IF NOT EXISTS public.job_categories (
            id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
            name TEXT NOT NULL,
            user_id UUID NOT NULL,
            created_at TIMESTAMPTZ DEFAULT CURRENT_TIMESTAMP
        );

        -- Create the job_category_mappings table if it doesn't exist
        CREATE TABLE IF NOT EXISTS public.job_category_mappings (
            job_id UUID REFERENCES public.jobs(id) ON DELETE CASCADE,
            category_id UUID REFERENCES public.job_categories(id) ON DELETE CASCADE,
            created_at TIMESTAMPTZ DEFAULT CURRENT_TIMESTAMP,
            PRIMARY KEY (job_id, category_id)
        );

        -- Enable RLS
        ALTER TABLE public.job_categories ENABLE ROW LEVEL SECURITY;
        ALTER TABLE public.job_category_mappings ENABLE ROW LEVEL SECURITY;

        -- Grant access to authenticated users
        GRANT ALL ON public.job_categories TO authenticated;
        GRANT ALL ON public.job_category_mappings TO authenticated;

        -- Create policy to allow users to manage their own categories
        CREATE POLICY IF NOT EXISTS "Users can manage their own categories"
        ON public.job_categories
        FOR ALL
        TO authenticated
        USING (auth.uid()::text = user_id::text)
        WITH CHECK (auth.uid()::text = user_id::text);

        -- Create policy to allow users to manage their own job category mappings
        CREATE POLICY IF NOT EXISTS "Users can manage their own job category mappings"
        ON public.job_category_mappings
        FOR ALL
        TO authenticated
        USING (
            auth.uid()::text IN (
                SELECT user_id::text FROM public.jobs WHERE id = job_id
            )
        )
        WITH CHECK (
            auth.uid()::text IN (
                SELECT user_id::text FROM public.jobs WHERE id = job_id
            )
        );
      `
    });
    
    if (createTableError) {
      console.error('Error creating categories tables:', createTableError);
      return false;
    }
    
    console.log('Successfully created categories tables');
    return true;
  } catch (error) {
    console.error('Unexpected error ensuring categories table:', error);
    return false;
  }
};

// Utility function to fix category table issues
export const fixCategoryTableIssues = async () => {
  try {
    console.log('Attempting to fix job_categories table issues...');
    
    // First check if we can get the session
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('Error getting session:', sessionError);
      return false;
    }
    
    if (!sessionData.session?.user?.id) {
      console.error('No user ID in session');
      return false;
    }
    
    const userId = sessionData.session.user.id;
    console.log('Current user ID:', userId);
    
    // Fix the table structure with type corrections and update policies
    const { error: fixError } = await supabase.rpc('execute_sql', {
      sql: `
        -- Drop existing policies to recreate them
        DROP POLICY IF EXISTS "Users can manage their own categories" ON public.job_categories;
        
        -- Add the UUID check constraint
        DO $$
        BEGIN
          -- Alter user_id column to UUID if it's not already
          IF EXISTS (
            SELECT 1 FROM information_schema.columns 
            WHERE table_name = 'job_categories' 
            AND column_name = 'user_id' 
            AND data_type != 'uuid'
          ) THEN
            ALTER TABLE public.job_categories ALTER COLUMN user_id TYPE UUID USING user_id::uuid;
          END IF;
        END $$;
        
        -- Create improved policy
        CREATE POLICY "Users can manage their own categories"
        ON public.job_categories
        FOR ALL
        TO authenticated
        USING (auth.uid()::text = user_id::text)
        WITH CHECK (auth.uid()::text = user_id::text);
        
        -- Insert test category to verify permissions
        INSERT INTO public.job_categories (name, user_id)
        VALUES ('Test Category - Delete Me', '${userId}'::uuid)
        ON CONFLICT DO NOTHING;
      `
    });
    
    if (fixError) {
      console.error('Error fixing category table:', fixError);
      return false;
    }
    
    console.log('Successfully fixed job_categories table issues');
    return true;
  } catch (error) {
    console.error('Unexpected error fixing category table:', error);
    return false;
  }
};

// Initial connection test
checkSupabaseConnection()
  .then(isConnected => {
    if (isConnected) {
      console.log('Supabase connection established successfully');
      // Check and ensure categories table exists
      return checkCategoriesTable()
        .then(categoryCheck => {
          if (!categoryCheck.exists) {
            console.log('Categories table does not exist, attempting to create it...');
            return ensureCategoriesTable();
          }
          // If table exists but we have RLS issues, try to fix them
          return fixCategoryTableIssues();
        });
    } else {
      console.warn('Supabase connection test failed - some features may not work correctly');
      return false;
    }
  })
  .then(tablesReady => {
    console.log('Database tables ready:', tablesReady);
  });

export const ensureTasksTable = async (): Promise<boolean> => {
  console.log('Ensuring tasks table exists...');
  
  try {
    // First, check if the table exists
    const { error: checkError } = await supabase.from('tasks').select('id').limit(1);
    
    // If we can query the table without errors, it exists
    if (!checkError) {
      console.log('Tasks table exists and is accessible');
      return true;
    }
    
    console.log('Creating tasks table...');
    
    // Create the tasks table if it doesn't exist
    const { error: createError } = await supabase.rpc('create_tasks_table_if_not_exists');
    
    if (createError) {
      console.error('Error creating tasks table via RPC:', createError);
      
      // Instead of trying to use SQL directly (which requires admin rights),
      // let's skip it and advise the user to run the migration
      console.log('Could not automatically create tasks table. Please run the migration in Supabase.');
      return false;
    }
    
    console.log('Tasks table created successfully');
    return true;
  } catch (error) {
    console.error('Error ensuring tasks table exists:', error);
    return false;
  }
};